import type {
  CampaignAnalyticsGranularity,
  CampaignAnalyticsResponse,
  SalesTrafficResponse,
} from "@adpilot/shared";
import { useTranslation } from "react-i18next";
import { ErrorState } from "@/components/shared/error-state";
import { AttributionNote } from "@/components/shared/performance/attribution-note";
import { useAttributionMix } from "@/hooks/use-attribution-note";
import { useShopScope } from "@/hooks/use-shop-scope";
import { useScopeAccountSplit } from "@/hooks/use-vendor-metric-availability";
import { buildDashboardPerformance, type DashboardPerformanceMetric } from "@/lib/dashboard-sales";
import { isSpApiMetric, scopeMetricAvailability } from "@/lib/vendor-metric-availability";
import { useReportingPeriodStore } from "@/stores/reporting-period";
import { useReportingTimeZoneStore } from "@/stores/reporting-time-zone";
import { DashboardFilterBar } from "./overview/dashboard-filter-bar";
import { KpiStrip, useDashboardKpiSlots } from "./overview/kpi-strip";
import type { DashboardCompareMode } from "./overview/kpi-strip-metrics";
import { PerformanceCard, usePerformanceChartPrefs } from "./overview/performance-card";
import { VendorCoverageNote } from "./overview/vendor-coverage-note";

interface SourceFreshness {
  state: "current" | "pending";
  updatedAt: string | null;
  throughDate: string | null;
}

interface DashboardOverviewSectionsProps {
  analytics: CampaignAnalyticsResponse | undefined;
  salesTraffic?: SalesTrafficResponse;
  salesLoading?: boolean;
  salesFetching?: boolean;
  salesError?: boolean;
  onSalesRetry?: () => void;
  startDate: string;
  endDate: string;
  compare: DashboardCompareMode;
  onCompareChange: (compare: DashboardCompareMode) => void;
  /** Applied child ASINs of the filter bar's ASIN filter. */
  asins: string[];
  onAsinsChange: (asins: string[]) => void;
  granularity: CampaignAnalyticsGranularity;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  onRetry: () => void;
  /** Inline CTA shown above the Sales retry when Ads is disconnected. */
  adsNotice?: React.ReactNode;
  /** Scope caveats shown beside the data freshness chip. */
  filterNotices?: React.ReactNode;
  /** Per-source freshness from `dashboard/data-freshness`; preferred over the report rows. */
  freshness?: { ads: SourceFreshness; sales: SourceFreshness };
  /** The freshness chip links to the data details in Settings. */
  canOpenDataDetails?: boolean;
  /** Card placed beside Performance (the AI status card in the Shop Dashboard design). */
  performanceAside?: React.ReactNode;
  /** Workspace target ACoS (fraction) for the Performance ACoS reference line. */
  targetAcos?: number | null;
}

/** Filter bar, KPI strip and the Performance chart over independently loaded Ads and Seller data. */
export function DashboardOverviewSections({
  analytics,
  salesTraffic,
  salesLoading = false,
  salesFetching = false,
  salesError = false,
  onSalesRetry,
  startDate,
  endDate,
  compare,
  onCompareChange,
  asins,
  onAsinsChange,
  granularity,
  isLoading,
  isFetching,
  isError,
  onRetry,
  adsNotice,
  filterNotices,
  freshness,
  canOpenDataDetails = false,
  performanceAside,
  targetAcos = null,
}: DashboardOverviewSectionsProps): React.ReactElement {
  const { t } = useTranslation("dashboard");
  const period = useReportingPeriodStore((state) => state.period);
  const setPeriod = useReportingPeriodStore((state) => state.setPeriod);
  const timeZone = useReportingTimeZoneStore((state) => state.timeZone);
  const setTimeZone = useReportingTimeZoneStore((state) => state.setTimeZone);
  const [slots, setSlots] = useDashboardKpiSlots();
  const performancePrefs = usePerformanceChartPrefs();
  const { scopeShopIds } = useShopScope();
  const attributionMix = useAttributionMix(scopeShopIds);
  // Vendor (1P) shops have no Seller Central data until Vendor Central lands (#679):
  // a vendor-only scope shows explained empty states for the SP-API backed metrics,
  // a mixed scope computes them from its sellers and says which shops they cover.
  const accounts = useScopeAccountSplit(scopeShopIds);
  const metricAvailability = (metric: DashboardPerformanceMetric) =>
    isSpApiMetric(metric) ? scopeMetricAvailability(accounts.mix, metric) : "available";
  const salesAvailability = scopeMetricAvailability(accounts.mix, "totalSales");
  const salesPending = salesAvailability !== "available";
  const model = buildDashboardPerformance({
    analytics,
    salesTraffic,
    startDate,
    endDate,
    granularity,
  });
  const kpis = model.kpis.map((kpi) => ({
    ...kpi,
    loading: isSpApiMetric(kpi.key) ? salesLoading && !salesPending : isLoading,
  }));
  const getLabel = (metric: DashboardPerformanceMetric) => t(`overview.kpi.metrics.${metric}`);

  // The earlier of the two sources is the date every number on the page covers. The
  // freshness endpoint knows when a source is still catching up; without it, fall back
  // to the report rows and the Seller sync runs.
  const earliest = (values: (string | null | undefined)[]) =>
    values.filter((value): value is string => Boolean(value)).sort()[0] ?? null;
  const dataThrough = freshness
    ? earliest([freshness.ads.throughDate, freshness.sales.throughDate])
    : earliest([analytics?.dataThrough, salesTraffic?.dataThrough]);
  const syncedAt = freshness
    ? earliest([freshness.ads.updatedAt, freshness.sales.updatedAt])
    : (salesTraffic?.shops
        .map((shop) => shop.lastSyncedAt)
        .filter((date): date is string => Boolean(date))
        .sort()
        .at(-1) ?? null);
  const syncing = freshness
    ? freshness.ads.state === "pending" || freshness.sales.state === "pending"
    : (salesTraffic?.shops.some(
        (shop) => shop.latestSync?.status === "queued" || shop.latestSync?.status === "processing",
      ) ?? false);

  // Sales and Ads load independently, and HTTP/schema failures get no toast, so the
  // chart keeps one compact retry per source.
  const adsErrorNotice = isError ? (
    <div role="alert">
      <ErrorState
        variant="inline"
        message={t("overview.performance.adsLoadFailed")}
        onRetry={onRetry}
        isRetrying={isFetching}
      />
    </div>
  ) : undefined;
  // A vendor-only scope expects no Seller Central sales, so a failed Sales read has
  // nothing to retry; the pending note below explains the missing series instead.
  const salesNotice =
    salesError && !salesPending ? (
      <div role="alert">
        <ErrorState
          variant="inline"
          message={t("salesTraffic.loadFailed")}
          onRetry={onSalesRetry}
          isRetrying={salesFetching}
        />
      </div>
    ) : undefined;
  const vendorNotice = salesPending ? (
    <p role="note" data-slot="vendor-sales-pending" className="text-xs text-muted-foreground">
      {t("overview.vendorData.performanceNote")}
    </p>
  ) : undefined;
  const notice =
    adsNotice || adsErrorNotice || salesNotice || vendorNotice ? (
      <div className="space-y-2">
        {adsNotice}
        {adsErrorNotice}
        {salesNotice}
        {vendorNotice}
      </div>
    ) : undefined;

  // The scope's attribution caveat sits with the other scope notices beside the
  // freshness chip: every number on the page shares that caveat (#671).
  // The coverage chip names the shops whose Seller Central sales are in the numbers:
  // once the Sales read is in, a seller without a grant is not covered either.
  const coveredSellers = salesTraffic
    ? accounts.sellers.filter((shop) =>
        salesTraffic.shops.some((row) => row.shopId === shop.shopId && row.connected),
      )
    : accounts.sellers;
  const chipClass =
    "flex h-[22px] items-center rounded-full border border-line bg-surface px-2 text-badge text-muted-foreground";
  // With no covered seller the KPIs already read "—"; a "covers 0 shops" chip adds nothing.
  const showCoverage = accounts.mix === "mixed" && coveredSellers.length > 0;
  const notices =
    attributionMix || showCoverage || filterNotices ? (
      <>
        {attributionMix ? <AttributionNote mix={attributionMix} className={chipClass} /> : null}
        {showCoverage ? (
          <VendorCoverageNote
            covered={coveredSellers}
            vendors={accounts.vendors}
            className={chipClass}
          />
        ) : null}
        {filterNotices}
      </>
    ) : undefined;

  return (
    <>
      <DashboardFilterBar
        dataThrough={dataThrough}
        syncedAt={syncedAt}
        syncing={syncing}
        linkToDataDetails={canOpenDataDetails}
        period={period}
        onPeriodChange={setPeriod}
        timeZone={timeZone}
        onTimeZoneChange={setTimeZone}
        compare={compare}
        onCompareChange={onCompareChange}
        asins={asins}
        onAsinsChange={onAsinsChange}
        notices={notices}
      />
      <KpiStrip
        kpis={kpis}
        trend={model.trend}
        slots={slots}
        onSlotsChange={setSlots}
        compare={compare}
        getLabel={getLabel}
        availability={metricAvailability}
      />
      <div className="flex min-w-0 flex-col gap-4 xl:flex-row">
        <PerformanceCard
          className="flex-1"
          trend={model.trend}
          kpis={kpis}
          view={performancePrefs.view}
          onViewChange={performancePrefs.setView}
          lowerMetric={performancePrefs.lowerMetric}
          onLowerMetricChange={performancePrefs.setLowerMetric}
          targetAcos={targetAcos}
          granularity={granularity}
          getLabel={getLabel}
          isLoading={isLoading && (salesLoading || salesPending)}
          notice={notice}
          salesAvailability={salesAvailability}
        />
        {performanceAside ? (
          <div className="flex min-w-0 shrink-0 xl:w-[380px]">{performanceAside}</div>
        ) : null}
      </div>
    </>
  );
}
